import {
  AlertCircle,
  AlertTriangle,
  CheckCircle2,
  Clock,
  FileQuestion,
  History,
  Loader2,
  MinusCircle,
  RotateCcw,
  XCircle,
} from 'lucide-react';
import {cn} from '@/lib/utils/cn';
import {DocumentStatus, type TransactionStatus} from '@/lib/api/types';

const STATUS_META: Record<
  TransactionStatus,
  {label: string; className: string; Icon: typeof CheckCircle2}
> = {
  [DocumentStatus.PENDING]: {
    label: 'Pending',
    className: 'bg-warning/15 text-warning ring-warning/30',
    Icon: Clock,
  },
  [DocumentStatus.PROCESSING]: {
    label: 'Processing',
    className: 'bg-info/15 text-info ring-info/30',
    Icon: Loader2,
  },
  [DocumentStatus.COMPLETED]: {
    label: 'Completed',
    className: 'bg-success/15 text-success ring-success/30',
    Icon: CheckCircle2,
  },
  [DocumentStatus.FAILED]: {
    label: 'Failed',
    className: 'bg-destructive/15 text-destructive ring-destructive/30',
    Icon: AlertCircle,
  },
  [DocumentStatus.SKIPPED]: {
    label: 'Skipped',
    className: 'bg-muted text-muted-foreground ring-border',
    Icon: MinusCircle,
  },
  [DocumentStatus.CANCELED]: {
    label: 'Canceled',
    className: 'bg-muted text-muted-foreground ring-border',
    Icon: XCircle,
  },
  [DocumentStatus.RETRY]: {
    label: 'Retry',
    className: 'bg-warning/15 text-warning ring-warning/30',
    Icon: RotateCcw,
  },
  [DocumentStatus.MISSING_DATA]: {
    label: 'Missing data',
    className: 'bg-warning/15 text-warning ring-warning/30',
    Icon: FileQuestion,
  },
  [DocumentStatus.TRANSFER_FAILED]: {
    label: 'Transfer failed',
    className: 'bg-destructive/15 text-destructive ring-destructive/30',
    Icon: AlertTriangle,
  },
  [DocumentStatus.IMS_UPDATE_FAILED]: {
    label: 'IMS update failed',
    className: 'bg-destructive/15 text-destructive ring-destructive/30',
    Icon: AlertTriangle,
  },
  [DocumentStatus.PREVIOUSLY_PROCESSED]: {
    label: 'Previously processed',
    className: 'bg-muted text-muted-foreground ring-border',
    Icon: History,
  },
};

interface StatusPillProps {
  status: TransactionStatus;
  className?: string;
}

export function StatusPill({status, className}: StatusPillProps) {
  const meta = STATUS_META[status] ?? STATUS_META[DocumentStatus.PENDING];
  const {Icon} = meta;
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-medium ring-1 ring-inset',
        meta.className,
        className
      )}
    >
      <Icon className="h-3 w-3" />
      {meta.label}
    </span>
  );
}
